import { useEffect, useState } from "react";
import { Phone, ArrowRight } from "lucide-react";
import { resolveImage } from "@/lib/api";

export default function Hero({ data, branding, onEnquire }) {
  const [idx, setIdx] = useState(0);
  const slides = data?.slides || [];
  const sideCard = data?.sideCard || {
    badge: "Estd. 2016",
    title: "A trusted pharma & healthcare education partner.",
    stats: [
      { value: "9+", label: "Years" },
      { value: "2500+", label: "Students" },
      { value: "15+", label: "Mentors" },
      { value: "100%", label: "Guidance" },
    ],
  };

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  if (!slides.length) return null;
  const slide = slides[idx];

  return (
    <section id="hero" data-testid="section-hero" className="relative min-h-[88vh] bg-[#0B1736] overflow-hidden pt-16 md:pt-20">
      {/* background image with overlay */}
      <div className="absolute inset-0" key={idx}>
        <img
          src={resolveImage(slide.image)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover hero-slide"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1736]/95 via-[#0B1736]/80 to-[#0B1736]/30" />
        <div className="absolute inset-0 grain opacity-30" />
      </div>

      {/* content */}
      <div className="relative container-pad min-h-[80vh] grid lg:grid-cols-12 items-center py-16 md:py-24">
        <div className="lg:col-span-7 text-white" key={`c-${idx}`}>
          <div className="hero-slide">
            <div className="kicker !text-teal-300 mb-5" data-testid="hero-kicker">{data.kicker || "Welcome"}</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
              {slide.title}
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-slate-200/90 leading-relaxed">
              {slide.subtitle}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                onClick={onEnquire}
                data-testid="hero-enquire-btn"
                className="btn-accent text-base"
              >
                {slide.primaryCta || "Enquire Now"} <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href={`tel:${branding?.primaryPhone || ""}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white/30 text-white text-sm font-semibold hover:bg-white hover:text-[#1E3A8A] transition"
              >
                <Phone className="w-4 h-4" /> {slide.secondaryCta || "Call Now"} {branding?.primaryPhone}
              </a>
            </div>
          </div>
        </div>

        {/* stat strip */}
        <div className="lg:col-span-5 mt-12 lg:mt-0 lg:pl-10">
          <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-3xl p-6 md:p-8 text-white shadow-2xl" data-testid="hero-side-card">
            <div className="text-xs tracking-[0.18em] uppercase text-teal-300 font-semibold">{sideCard.badge || "Estd. 2016"}</div>
            <div className="mt-3 text-2xl md:text-3xl font-bold leading-snug">
              {sideCard.title || "A trusted pharma & healthcare education partner."}
            </div>
            <div className="mt-6 grid grid-cols-2 gap-5">
              {(sideCard.stats || []).map((s, i) => (
                <Stat key={i} value={s.value} label={s.label} testid={`hero-stat-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* slide indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-10 bg-teal-400" : "w-4 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-2xl md:text-3xl font-extrabold text-teal-300">{value}</div>
      <div className="text-xs text-slate-300 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}
